import { useAddToCart, useCart } from '@corratech/pylot-cart-manager'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import { getProductsBySku } from '@pylot-data/api/operations/get-product'
import { useTranslation } from 'next-i18next/pages'
import { useCallback, useEffect } from 'react'
import type { ProductInterface } from '@pylot-data/pylotschema'
interface ExponeaProductEvent {
    sku: string
    uid: string
    quantity: number
    name: string
    options: []
}

const BLOOMREACH_EVENT = 'BloomreachAddToCart'

interface CachedFetches {
    [key: string]: ProductInterface
}
const cachedFetches = {} as CachedFetches
const cachedFetch = async (sku: string) => {
    if (!cachedFetches[sku]) {
        try {
            const productsResult = await getProductsBySku([sku], 'es')
            const [product] = productsResult.products.items
            cachedFetches[sku] = product
        } catch (e) {
            return { error: 'An error occured', data: null }
        }
    }
    return { data: cachedFetches[sku], error: null }
}

function useExponeaCarousel(): void {
    const { data } = useCart()
    const { addToCart } = useAddToCart()
    const { openCartToast, openSidebar, openToast } = useUI()
    const { t } = useTranslation()

    const addProductHandler = useCallback(
        async (
            sku: string,
            uid: string,
            quantity: number,
            name: string,
            options: []
        ) => {
            if (!data) {
                openToast(
                    t(
                        'common||Your cart is loading, please try again in a few seconds!'
                    ),
                    ToastType.Warning
                )
                return
            }

            try {
                const productsResult = await cachedFetch(sku)
                if (productsResult.error && !productsResult.data) {
                    openToast(
                        t('Something went wrong. Please try again'),
                        ToastType.Danger
                    )
                    return
                }

                const response = await addToCart(
                    [
                        {
                            sku,
                            uid,
                            selected_options: options,
                            quantity
                        }
                    ],
                    [productsResult.data!],
                    undefined,
                    true
                )

                openSidebar()

                const errorMessage =
                    response?.errors?.[0]?.message ??
                    response?.user_errors?.[0]?.message ??
                    null
                const successMessage = t('cart|{{name}} was added to cart', {
                    name
                })

                if (response?.data && !errorMessage) {
                    const { cart } = response.data
                    cartUpdateEvent('add', null, cart, sku)
                }

                setTimeout(
                    () =>
                        openCartToast(
                            errorMessage ? errorMessage : successMessage,
                            errorMessage ? ToastType.Warning : ToastType.Success
                        ),
                    300
                )
            } catch (e) {
                openToast(
                    t('Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        },
        [data]
    )

    useEffect(() => {
        const addExponeaProductToCartHandler = (
            e: CustomEvent<ExponeaProductEvent>
        ) => {
            const { sku, uid, quantity, name, options } = e.detail
            addProductHandler(sku, uid, quantity, name, options)
        }

        document.addEventListener(
            BLOOMREACH_EVENT,
            addExponeaProductToCartHandler as EventListener
        )

        return () =>
            document.removeEventListener(
                BLOOMREACH_EVENT,
                addExponeaProductToCartHandler as EventListener
            )
    }, [addProductHandler])
}

export { useExponeaCarousel }
